<script lang="ts" setup>

    import { ref, watch, onMounted, computed, nextTick } from "vue";
    import { ISearchBaseColumnItem, ISearchBaseInputs } from "../../lib/Models/SearchModel";
    import { ChoiceUtils } from "../../LibMain";
    import Icon from '../../components/Icon.vue';
    import { DisplayStyle, IconTypes } from '../../Enums/UiEnums';
    import { ISearchBaseProps } from './SearchUtils';
    import _ from "lodash";
     
    const props = withDefaults(defineProps<ISearchBaseProps>(), {
        placeholder: "",
        label: "",
        defaultSearchText: "",
        maxHeight: 200,
        processing: false,
        resetFocus: false,
        enableAutoSearch: false, 
        showClearSearch: false, 
        addToBaseClass: "",
        disabled: false,
        newButton: false 
    });
    
    const emit = defineEmits<{
        (e: "performSearchBase", inputs: ISearchBaseInputs): void,
        (e: "onChange", selectedItem: any): void,
        (e: "update:processing", newValue: boolean): void,
        (e: "update:searchMode", newValue: string): void,
        (e: "new", newValue: boolean): void,
    }>();

    defineExpose({focus, onSearch, clearSearch});

    const displayResults = ref<boolean>(false);
    const searchText = ref<string>("");
    const moreResults = ref<boolean>(false);
    const allResults = ref<boolean>(false);
    const processing = ref<boolean>(false);
    const selectedText = ref<string>("");
    const myInput = ref<HTMLInputElement>();

    onMounted(() => {
        nextTick(() => {
                if (props.resetFocus ?? false) {
                    focus();
                }
            });
    });

    watch(() => props.searchResults, (newValue, oldValue) => {
        displayResults.value = true;
        moreResults.value = false;
        processing.value = false;
        emit("update:processing", processing.value);
        if (!newValue)
            return;
        if ((newValue.length == 1) && (isNaN(+searchText.value))) {
            if (props.displayColumn.includes("|")) {
                const columns = props.displayColumn.split("|");
                const display = columns.map(column => newValue[0][column] != null ? newValue[0][column] : "");
                searchText.value = display.join(" ");
            }
            else {
                searchText.value = newValue[0][props.displayColumn];
            }
            displayResults.value = false;
            selectedText.value = searchText.value;    
            emit("onChange", newValue[0]);
        }
        if (newValue.length >= 50 && !allResults.value) {
            moreResults.value = true;
        }      
    });
    watch(() => props.defaultSearchText, (newValue, oldValue) => {
        displayResults.value = false;
        searchText.value = newValue;
        selectedText.value = newValue;
    });
    watch(() => props.processing, (newValue, oldValue) => {
        processing.value = newValue;
    });

    const searchButtonId = computed<string>(() => {
        return `${ChoiceUtils.randomId()}SearchButton`;
    });
    const searchTextId = computed<string>(() => {
        return `${ChoiceUtils.randomId()}SearchText`;
    });
    const displayLabel = computed<boolean>(() => (props.label ?? '').length > 0 ? true : false);
    const displayFooterResults = computed<boolean>(() => {
        if (props.searchResults) {
            if (props.searchResults.length > 0) {
                return !moreResults.value;
            }
        }
        return true;    
    });
    const footerText = computed<string>(() => {
        if (props.searchResults) {
            if (props.searchResults.length > 0)
                return `All Results Displayed (${props.searchResults.length})`;
        }
        return "No Results Found";
    });    

    const showClearButton = computed<boolean>(() => {
        return (props.showClearSearch) && ((selectedText.value.length ?? 0) > 0)
    });
    
    const inputBaseClass = computed<string>(() => `form-control ${props.addToBaseClass}`);
    const searchResultsBaseClass = computed<string>(() => `table table-bordered table-hover small ${props.addToBaseClass}`);
    const labelBaseClass = computed<string>(() => props.addToBaseClass);
    const buttonBaseClass = computed<string>(() => `btn btn-primary ${props.addToBaseClass}`);
    const spinnerBaseClass = computed<string>(() => `spinner-border spinner-border-sm ${props.addToBaseClass}`);
    const footerLabelBaseClass = computed<string>(() => `text-muted ${props.addToBaseClass}`);
    const footerButtonBaseClass = computed<string>(() => `btn btn-outline-primary btn-sm ${props.addToBaseClass}`);
    const searchModeButtonClass = computed<string>(() => `btn btn-outline-primary dropdown-toggle`);

    async function onAutoSearch() {
        if (props.enableAutoSearch) {
            await onSearch(false);
        }
    }

    async function onSearch(displayAllResults: boolean = false) {
        if (processing.value)
            return;
        allResults.value = displayAllResults;
        const searchInputs: ISearchBaseInputs = { searchText: searchText.value, displayAllResults: displayAllResults };
        processing.value = true;
        emit("update:processing", processing.value);
        emit("performSearchBase", searchInputs);
    }

    function onSelect(selectedItem: any) {
        if (props.performAllowSelectedItemCheck)
        {
            if (!props.performAllowSelectedItemCheck(selectedItem))
                return;
        }

        if (props.displayColumn.includes("|")) {
            const columns = props.displayColumn.split("|");
            const display = columns.map(column => selectedItem[column] != null ? selectedItem[column] : "");
            searchText.value = display.join(" ");
        }
        else {
            searchText.value = selectedItem[props.displayColumn];
        }
        displayResults.value = false;
        selectedText.value = searchText.value;  
        emit("onChange", selectedItem);
    }

    function onCloseSearch() {
        displayResults.value = false;
    }
    function onNewSearch() {
        emit("new", true);
    }
    async function onFocusOut() {
        if (searchText.value === "") { 
            emit("onChange", null);
        }
        setTimeout(() => {
            if (searchText.value != selectedText.value) {
                // If the user has selected a search row and then changes the search text without reselecting, then clear the selected search
                if (searchText.value != "")
                    onSearch(false);
                else
                    processing.value = false;
            }
        }, 200);
    }
    function focus()
    {
        const input = myInput.value;
        input?.focus();
    }

    function clearSearch() {
        selectedText.value = "";
        searchText.value = "";
        emit("onChange", undefined);
        onCloseSearch();        
        focus();
    }

    function styleMaxHeight() {
        return `height: ${props.maxHeight ?? 200}px`;
    }

    const currentSearchMode = computed(() => props.searchMode ?? _.first(props.searchModes));

    const computedTitle = computed(() => {
        if ((props?.title?.length ?? 0 )> 0) 
            return props.title;
        return searchText.value;
    }
    );
</script>

<template>
    <label v-if="displayLabel" :for="searchTextId" :class="labelBaseClass">{{label}}</label> 
    <div class="input-group">        
        <input type="text" 
                autocomplete="off" 
                :class="inputBaseClass" 
                :id="searchTextId" 
                :disabled="props.disabled" 
                :placeholder="placeholder" 
                :aria-label="placeholder" 
                :aria-describedby="searchButtonId" 
                ref="myInput" 
                :title="computedTitle"
                v-model="searchText" 
                v-on:focusout="onFocusOut()" 
                v-on:keyup.enter="onSearch(false)"
                @input="onAutoSearch">
        
        <template v-if="showClearButton">
            <span class="border" title="Clear Search" @click="clearSearch" style="cursor: pointer; background-color: #eee;">
                <Icon :icon="IconTypes.Close" :display-style="DisplayStyle.Secondary" class="ms-3 me-3 mt-2"/>
            </span> 
        </template> 

        <!-- Search Modes dropdown -->
        <template v-if="props.searchModes">
            <button :class="searchModeButtonClass" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                {{currentSearchMode}}
            </button>        
            <ul class="dropdown-menu dropdown-menu-end">
                <li v-for="searchMode in props.searchModes">
                    <span class="dropdown-item" @click="() => emit('update:searchMode', searchMode) ">{{searchMode}}</span>
                </li>
            </ul>
        </template>

        <button :class="buttonBaseClass" type="button" :id="searchButtonId" :disabled="props.disabled" @click="onSearch(false)">
            Search
            <span v-show="processing" :class="spinnerBaseClass" role="status" aria-hidden="true"></span>
        </button>
    </div>

    <!-- Search Results -->
    <div v-if="displayResults" class="overflow-auto" :style="styleMaxHeight()">
        <slot name="searchResults" :searchResults="searchResults">
            <table :class="searchResultsBaseClass" aria-label="Search Results">
                <thead>
                    <tr>
                        <th v-for="col in columns">{{col.title}}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="result in searchResults" title="Click to select this entry" style="cursor: pointer" @click="onSelect(result)">
                        <td v-for="col in columns" :class="col.classStyle">
                            <slot name="cell" :element="col.element" :title="col.title" :row="result" :col="col">
                                <span v-if="col.isHtml" v-html="result[col.element]"/>
                                <template v-if="!col.isHtml">
                                    {{ result[col.element] }}
                                </template>
                            </slot>
                        </td>
                    </tr>
                </tbody>
            </table>
        </slot>
    </div>
    <div v-if="displayResults" class="row align-items-center mt-2">
        <div class="col">
            <span :class="footerLabelBaseClass" v-if="displayFooterResults">{{footerText}}</span>
            <button v-if="!displayFooterResults" type="button" :class="footerButtonBaseClass" @click="onSearch(true)">Display All Results</button>        
        </div>
        <div v-if="props.newButton" class="col-md-auto">
            <button type="button" :class="footerButtonBaseClass" @click="onNewSearch()">New</button>
        </div>
        <div class="col-md-auto">
            <button type="button" :class="footerButtonBaseClass" @click="onCloseSearch()">Close Search</button>
        </div>
    </div>
</template>